import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, IconButton, Tooltip, CircularProgress, Chip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import { getAppointments } from '../services/api';
import { APPOINTMENT_STATE_CONFIG as STATE_CONFIG } from '../constants/appointmentStates';
import AppointmentDialog from './AppointmentDialog';
import { useAuth } from '../hooks/useAuth';

// ── Constants ──────────────────────────────────────────────────────────────

// Paleta de colores suaves para identificar médicos visualmente
const DOCTOR_COLORS = [
  '#1565c0', '#2e7d32', '#6a1b9a', '#c62828',
  '#e65100', '#00695c', '#4527a0', '#558b2f',
];

const SLOT_MINUTES = 30;          // granularity
const DAY_START_HOUR = 7;         // 07:00
const DAY_END_HOUR = 21;          // 21:00
const SLOT_HEIGHT = 40;           // px per slot
const TIME_COL_WIDTH = 52;        // px

const TOTAL_SLOTS = ((DAY_END_HOUR - DAY_START_HOUR) * 60) / SLOT_MINUTES;

// ── Helpers ────────────────────────────────────────────────────────────────

const toLocalDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const MONTH_NAMES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

const minutesSinceStart = (dateUTC) => {
  const local = new Date(dateUTC.getTime());
  const h = local.getHours(), m = local.getMinutes();
  return (h - DAY_START_HOUR) * 60 + m;
};

const slotTop = (apStartUTC) => {
  const mins = minutesSinceStart(apStartUTC);
  return (mins / SLOT_MINUTES) * SLOT_HEIGHT;
};

const slotHeight = (durationMinutes) => (durationMinutes / SLOT_MINUTES) * SLOT_HEIGHT;

const getColor = (ap) => {
  if (ap.is_overbooked) return '#e65100';
  return STATE_CONFIG[ap.state]?.color ?? '#78909c';
};

// ── Time column labels ─────────────────────────────────────────────────────

const TimeColumn = () => (
  <Box sx={{ width: TIME_COL_WIDTH, flexShrink: 0, position: 'relative', height: TOTAL_SLOTS * SLOT_HEIGHT }}>
    {Array.from({ length: TOTAL_SLOTS }).map((_, i) => {
      const totalMins = DAY_START_HOUR * 60 + i * SLOT_MINUTES;
      const h = Math.floor(totalMins / 60), m = totalMins % 60;
      const showLabel = m === 0;
      return (
        <Box key={i} sx={{
          position: 'absolute', top: i * SLOT_HEIGHT, left: 0, width: '100%', height: SLOT_HEIGHT,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
          pr: 0.75, pt: 0.25,
        }}>
          {showLabel && (
            <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.disabled', lineHeight: 1 }}>
              {String(h).padStart(2, '0')}:00
            </Typography>
          )}
        </Box>
      );
    })}
  </Box>
);

// ── Doctor column ──────────────────────────────────────────────────────────

const DoctorColumn = ({ doctor, appointments, onSlotClick, onEventClick, canEdit, color }) => {
  const nowRef = useRef(null);

  const now = new Date();
  const nowMins = minutesSinceStart(now);
  const nowTop = (nowMins / SLOT_MINUTES) * SLOT_HEIGHT;
  const showNow = nowMins >= 0 && nowMins <= (TOTAL_SLOTS * SLOT_MINUTES);

  return (
    <Box sx={{ flex: 1, minWidth: 160, position: 'relative', borderLeft: `3px solid ${color}` }}>
      {/* Slot grid lines */}
      {Array.from({ length: TOTAL_SLOTS }).map((_, i) => (
        <Box key={i} sx={{
          position: 'absolute', top: i * SLOT_HEIGHT, left: 0, right: 0,
          height: SLOT_HEIGHT,
          borderTop: i % 2 === 0 ? '1px solid' : '1px dashed',
          borderColor: i % 2 === 0 ? 'divider' : 'rgba(0,0,0,0.06)',
          cursor: canEdit ? 'pointer' : 'default',
          '&:hover': canEdit ? { bgcolor: 'action.hover' } : {},
        }}
          onClick={() => {
            if (!canEdit) return;
            const totalMins = DAY_START_HOUR * 60 + i * SLOT_MINUTES;
            onSlotClick(doctor, totalMins);
          }}
        />
      ))}

      {/* Now indicator */}
      {showNow && (
        <Box ref={nowRef} sx={{
          position: 'absolute', top: nowTop, left: 0, right: 0,
          height: 2, bgcolor: 'error.main', zIndex: 3,
          '&::before': {
            content: '""', position: 'absolute', left: -3, top: -3,
            width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main',
          },
        }} />
      )}

      {/* Appointments */}
      {appointments.map((ap) => {
        const startUTC = new Date(ap.start_time + 'Z');
        const top = slotTop(startUTC);
        const height = Math.max(slotHeight(ap.duration_minutes), 20);
        const color = getColor(ap);
        const stateLabel = STATE_CONFIG[ap.state]?.label || ap.state;

        return (
          <Tooltip key={ap.id}
            title={`${ap.patient?.name || 'Bloqueado'} — ${stateLabel} (${ap.duration_minutes} min)`}
            placement="top" arrow>
            <Box onClick={(e) => { e.stopPropagation(); onEventClick(ap); }}
              sx={{
                position: 'absolute', top, left: 3, right: 3,
                height: height - 2,
                bgcolor: color, borderRadius: 1,
                cursor: 'pointer', zIndex: 2,
                overflow: 'hidden',
                px: 0.75, pt: 0.25,
                boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                '&:hover': { filter: 'brightness(0.92)', boxShadow: '0 2px 6px rgba(0,0,0,0.3)' },
                transition: 'filter 0.1s, box-shadow 0.1s',
              }}>
              {!!ap.checked_in_at && (
                <Typography component="span" sx={{ fontSize: '0.6rem', color: 'white', mr: 0.3, fontWeight: 900 }}>✓</Typography>
              )}
              <Typography variant="caption" sx={{ color: 'white', fontWeight: 600, fontSize: '0.68rem', lineHeight: 1.2 }} noWrap>
                {ap.patient?.name || 'Bloqueado'}
              </Typography>
              {height >= 36 && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.62rem', display: 'block', lineHeight: 1 }}>
                  {stateLabel}
                </Typography>
              )}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

// ── Main component ─────────────────────────────────────────────────────────

const MultiDoctorDayView = ({ doctors }) => {
  const { isAdmin, isRecepcionista } = useAuth();
  const canEdit = isAdmin || isRecepcionista;

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState({});  // { doctorId: [ap, ...] }
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  const dateStr = toLocalDateStr(selectedDate);

  const load = useCallback(async () => {
    if (doctors.length === 0) return;
    setLoading(true);
    const startOfDay = new Date(selectedDate);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(selectedDate);
    endOfDay.setHours(23, 59, 59, 999);

    try {
      const results = await Promise.all(
        doctors.map(d => getAppointments({
          doctor_id: d.id,
          start_date: startOfDay.toISOString(),
          end_date: endOfDay.toISOString(),
          limit: 500,
        }).then(r => ({ doctorId: d.id, items: r.data?.items || [] }))
          .catch(() => ({ doctorId: d.id, items: [] }))
        )
      );
      const byDoctor = {};
      results.forEach(({ doctorId, items }) => { byDoctor[doctorId] = items; });
      setAppointments(byDoctor);
    } finally {
      setLoading(false);
    }
  }, [doctors, selectedDate]);

  useEffect(() => { load(); }, [load]);

  const goToday = () => setSelectedDate(new Date());
  const goPrev = () => setSelectedDate(d => { const n = new Date(d); n.setDate(n.getDate() - 1); return n; });
  const goNext = () => setSelectedDate(d => { const n = new Date(d); n.setDate(n.getDate() + 1); return n; });

  const handleSlotClick = (doctor, totalMins) => {
    const h = Math.floor(totalMins / 60), m = totalMins % 60;
    const dt = new Date(selectedDate);
    dt.setHours(h, m, 0, 0);
    // CalendarGrid passes dateStr without timezone so dialog interprets as local
    const pad = (n) => String(n).padStart(2, '0');
    const localStr = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(h)}:${pad(m)}:00`;
    setIsEdit(false);
    setSelectedEvent({ start_time: localStr, doctor_id: doctor.id });
    setDialogOpen(true);
  };

  const handleEventClick = (ap) => {
    setIsEdit(true);
    setSelectedEvent(ap);
    setDialogOpen(true);
  };

  const isToday = toLocalDateStr(new Date()) === dateStr;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Date nav */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
        <IconButton size="small" onClick={goPrev}><ChevronLeftIcon fontSize="small" /></IconButton>
        <IconButton size="small" onClick={goNext}><ChevronRightIcon fontSize="small" /></IconButton>
        <Typography variant="body2" fontWeight={700} sx={{ minWidth: 220 }}>
          {DAY_NAMES[selectedDate.getDay()]}, {selectedDate.getDate()} de {MONTH_NAMES[selectedDate.getMonth()]} {selectedDate.getFullYear()}
        </Typography>
        {isToday && <Chip label="Hoy" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />}
        <Tooltip title="Ir a hoy">
          <IconButton size="small" onClick={goToday}><TodayIcon fontSize="small" /></IconButton>
        </Tooltip>
        {loading && <CircularProgress size={16} sx={{ ml: 1 }} />}
      </Box>

      {/* Doctor headers */}
      <Box sx={{ display: 'flex', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.100' }}>
        <Box sx={{ width: TIME_COL_WIDTH, flexShrink: 0 }} />
        {doctors.map((d, idx) => {
          const docColor = DOCTOR_COLORS[idx % DOCTOR_COLORS.length];
          return (
            <Box key={d.id} sx={{
              flex: 1, minWidth: 160, py: 1, px: 1,
              borderLeft: `3px solid ${docColor}`,
              textAlign: 'center',
            }}>
              <Typography variant="body2" fontWeight={700} noWrap sx={{ color: docColor }}>{d.name}</Typography>
              {d.specialty && (
                <Typography variant="caption" color="text.secondary" noWrap>{d.specialty}</Typography>
              )}
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
                {(appointments[d.id] || []).length} turno{(appointments[d.id] || []).length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Grid body */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', minWidth: TIME_COL_WIDTH + doctors.length * 160 }}>
          <TimeColumn />
          {doctors.map((d, idx) => (
            <DoctorColumn
              key={d.id}
              doctor={d}
              appointments={appointments[d.id] || []}
              onSlotClick={handleSlotClick}
              onEventClick={handleEventClick}
              canEdit={canEdit}
              color={DOCTOR_COLORS[idx % DOCTOR_COLORS.length]}
            />
          ))}
        </Box>
      </Box>

      {dialogOpen && (
        <AppointmentDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          isEdit={isEdit}
          selectedEvent={selectedEvent}
          doctorId={selectedEvent?.doctor_id || (doctors[0]?.id)}
          patients={patients}
          setPatients={setPatients}
          onSaved={() => { setDialogOpen(false); load(); }}
        />
      )}
    </Box>
  );
};

export default MultiDoctorDayView;
